import { Component, Prop, h, Host, Element, Listen } from '@stencil/core';

@Component({
  tag: 'pn-filter-checkbox',
  styleUrl: 'pn-filter-checkbox.scss',
  shadow: true,
})
export class PnFilterCheckbox {
  /** This will be emitted on change and input, like a native checkbox */
  @Prop({ reflect: true }) value: any;
  /** The name of the checkbox group */
  @Prop() name: string;
  /** The string you put here will be what you'd make the ID of a native radio button, you can't name props "id" because it's reserved
   * so we went with the more descriptive name "checkboxid", if you want to create labels for your checkbox, you will bind it to this ID
   */
  @Prop() checkboxid: string;
  @Prop() disabled?: boolean = false;
  @Prop() checked?: boolean = false;
  @Prop({ mutable: true }) indeterminate?: boolean = false;

  @Element() hostElement: HTMLElement;

  @Listen('change')
  handlechange() {
    this.indeterminate = false;
  }

  componentDidLoad() {
    this.checkAndSetIndeterminateState();
    this.bindEvents();
  }

  componentDidUpdate() {
    this.checkAndSetIndeterminateState();
  }

  bindEvents() {
    const labels = document.querySelectorAll('label[for="' + this.checkboxid + '"]');
    labels.forEach(label => {
      label.addEventListener('click', () => {
        this.hostElement.shadowRoot.querySelector('label').click();
      });
    });

    const checkboxInput = this.hostElement.shadowRoot.querySelector('input');
    checkboxInput.addEventListener('change', this.onCheckboxChange.bind(this, checkboxInput));
  }

  onCheckboxChange(checkboxInput: HTMLInputElement) {
    let outsideInput: HTMLInputElement = this.hostElement.querySelector('input.hidden-' + this.checkboxid) as HTMLInputElement | null;
    if (outsideInput) {
      outsideInput.value = checkboxInput.checked ? this.value : '';
      outsideInput.dispatchEvent(new CustomEvent('change', { detail: outsideInput }));
    }
  }

  checkAndSetIndeterminateState() {
    let checkbox = this.hostElement.querySelector(`#${this.checkboxid}`) as HTMLInputElement;

    if (checkbox) {
      checkbox.indeterminate = this.indeterminate;
    }
  }

  private renderInputOutsideShadowRoot() {
    let input: HTMLInputElement = this.hostElement.querySelector('input.hidden-' + this.checkboxid) as HTMLInputElement | null;
    if (!input) {
      input = this.hostElement.ownerDocument.createElement('input');
      input.type = 'hidden';
      input.classList.add('hidden-' + this.checkboxid);
      this.hostElement.appendChild(input);
    }

    input.name = this.name ?? this.checkboxid;
  }

  render() {
    this.renderInputOutsideShadowRoot();

    return (
      <Host checked={this.checked} id={this.checkboxid}>
        <input type="checkbox" id={this.checkboxid} value={this.value} name={this.name || this.checkboxid} disabled={this.disabled} checked={this.checked} />
        <label htmlFor={this.checkboxid} class="pn-checkbox">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
            <polyline class="checkmark-path" points="4,12 9,17 20,6" stroke-width="3" />
            <polyline class="indeterminate-path" points="4,12 20,12" stroke-width="3" />
          </svg>
        </label>
      </Host>
    );
  }
}
